import { useState } from 'react';
import { useQuery } from '@apollo/client';

import AssetSummaryInfo from '@atom/components/common/map/mapDrawerContent/AssetSummaryInfo';
import AssetSummaryWork from '@atom/components/common/map/mapDrawerContent/AssetSummaryWork';
import NavigationControls from '@atom/components/common/map/mapDrawerContent/NavigationControls';
import { useWorkOrdersData } from '@atom/components/mapPortal/hooks/workOrdersDataHook';
import { GET_ASSET } from '@atom/graph/asset';
import { SearchParamType, useNavigation } from '@atom/hooks/useNavigation';
import { Icon, IconButton, Progress } from '@atom/mui';
import { FILES_ENDPOINT } from '@atom/utilities/endpoints';
import { getStaticImage } from '@atom/utilities/mapUtilities';

import { State } from '../InventoryPortal';

import InventoryNavigationButton from './InventoryNavigationButton';

import classes from './inventoryMap.module.css';

const views = [
  {
    value: 'info',
    icon: 'info',
  },
  {
    value: 'work',
    icon: 'work',
  },
];

const styles = {
  iconContainer: {
    width: 24,
    height: 24,
    padding: 0,
  },
  progress: {
    height: '100%',
  },
};

type Props = {
  portalState: State;
};

const InventoryMapDetail = ({ portalState }: Props) => {
  const [{ id }, { updateSearchParams }] = useNavigation({
    id: SearchParamType.STRING,
  });
  const [view, setView] = useState<string>('info');
  const [workOrders, woLoading] = useWorkOrdersData({
    assetId: id,
    statusIds: '1,2,3,4,5',
    sortBy: 'status',
  });

  const { loading, data, refetch } = useQuery(GET_ASSET, {
    variables: { id },
    fetchPolicy: 'network-only',
  });

  const getSummaryImage = () => {
    return !data?.asset?.mainPhotoFileId
      ? getStaticImage(data?.asset?.location)
      : `${FILES_ENDPOINT}/${data?.asset?.mainPhotoFileId}_340`;
  };

  const getTrayContentBody = () => {
    switch (view) {
      case 'info': {
        return <AssetSummaryInfo asset={data?.asset} refetch={refetch} />;
      }
      case 'work': {
        return <AssetSummaryWork work={workOrders} asset={data?.asset} />;
      }
      default: {
        return <AssetSummaryInfo asset={data?.asset} refetch={refetch} />;
      }
    }
  };

  const body = getTrayContentBody();

  const setActiveView = activeView => {
    setView(activeView);
  };

  return (
    <div className={classes.container}>
      {loading || woLoading ? (
        <Progress style={styles.progress} />
      ) : (
        <div className={classes.trayContainer}>
          <div className={classes.headerContainer}>
            <div className={classes.headerTitleContainer}>
              <div className={classes.headerTitle}>{data?.asset.name}</div>
              <div className={classes.exitContainer}>
                <IconButton
                  style={styles.iconContainer}
                  onClick={() => updateSearchParams({ id: '' })}
                >
                  <Icon>close</Icon>
                </IconButton>
              </div>
            </div>
          </div>
          <div className={classes.trayContentContainer}>
            <div
              className={classes.summaryImageContainer}
              style={{
                backgroundImage: `url('${getSummaryImage()}')`,
              }}
            />
            <NavigationControls
              views={views}
              activeView={view}
              setActiveView={setActiveView}
            />
            <div>{body}</div>
          </div>
          <InventoryNavigationButton
            label="View Full Detail"
            portalState={portalState}
          />
        </div>
      )}
    </div>
  );
};

export default InventoryMapDetail;
